import {
  Box,
  Chip,
  Link,
  Stack,
  Accordion,
  Typography,
  AccordionDetails,
  AccordionSummary,
  Divider,
} from '@mui/material';

import Iconify from '../iconify';
import { RadialBar, RadialBarThreshold } from '../score-chart';
import { CarbonScoreType, ResolvedCarbonScore, CarbonIntensityBasis } from '../../models';

interface Props {
  carbonScore: ResolvedCarbonScore | null;
}

const CARBON_HELP_URL =
  'https://equilibrium-markets.atlassian.net/wiki/spaces/MP/pages/2690449409/Understanding+Carbon+Scores';

const CARBON_DISCLAIMER =
  'Carbon scores shown in AgileChain are indicative estimates designed to support procurement and strategic decision-making. They are not independently verified emissions calculations and are not intended to be used for formal carbon footprint reporting.';

const INTENSITY_SCALE_MAX = 25;

// Bands follow typical food lifecycle intensities: plant products mostly sit under 3,
// eggs/poultry/pork around 3–10, red meat/cheese above 10 kg CO₂e per kg.
const INTENSITY_BANDS = [
  { upTo: 3, rating: 'Low', color: 'success' },
  { upTo: 10, rating: 'Medium', color: 'warning' },
  { upTo: INTENSITY_SCALE_MAX, rating: 'High', color: 'error' },
] as const;

const INTENSITY_THRESHOLDS: RadialBarThreshold[] = INTENSITY_BANDS.map((band) => ({
  upTo: band.upTo,
  color: (theme) => theme.palette[band.color].main,
}));

const bandFor = (value: number) =>
  INTENSITY_BANDS.find((band) => value <= band.upTo) ?? INTENSITY_BANDS[INTENSITY_BANDS.length - 1];

const QUALITY_CHIP_MAP: Record<
  CarbonScoreType,
  { label: string; color: 'success' | 'warning' | 'error' }
> = {
  supplierData: { label: 'Supplier Data', color: 'success' },
  proxyData: { label: 'Proxy Data', color: 'warning' },
  industryBenchmark: { label: 'Benchmark Data', color: 'error' },
};

const DESCRIPTION_MAP: Record<CarbonScoreType, string> = {
  supplierData: 'Lifecycle emissions entered directly by the supplier for this product.',
  proxyData:
    'Lifecycle emissions estimated from a closely matching product in an external emissions database.',
  industryBenchmark:
    'Lifecycle emissions based on an industry benchmark for this product category.',
};

const BASIS_UNIT_MAP: Record<CarbonIntensityBasis, string> = {
  [CarbonIntensityBasis.PerKg]: 'kg',
  [CarbonIntensityBasis.PerLitre]: 'L',
};

function DetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <Box>
      <Typography variant="body2" display="block">
        <strong>{label}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {value}
      </Typography>
    </Box>
  );
}

export default function CarbonScoreChart({ carbonScore }: Props) {
  const intensity = carbonScore?.carbonIntensityKgCo2e ?? null;
  const footprint = carbonScore?.carbonScoreKgCo2e ?? null;
  const proxyData =
    carbonScore?.scoreType === CarbonScoreType.ProxyData ? carbonScore.proxyCarbonScoreData : null;

  const band = intensity !== null ? bandFor(intensity) : null;
  const caption =
    intensity !== null && carbonScore
      ? `${Number(intensity.toFixed(2))} kg CO₂e/${BASIS_UNIT_MAP[carbonScore.intensityBasis]}`
      : 'No Data';

  return (
    <Accordion>
      <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" />}>
        <Stack gap={1}>
          <Stack direction="row" alignItems="center" gap={1}>
            <RadialBar
              value={intensity}
              max={INTENSITY_SCALE_MAX}
              size={100}
              hollowSize="50%"
              label={
                band && (
                  <Box component="span" sx={{ fontSize: 13 }}>
                    {band.rating}
                  </Box>
                )
              }
              ariaLabel={band ? `${caption} · ${band.rating}` : caption}
              thresholds={INTENSITY_THRESHOLDS}
            />
            <Stack>
              <Typography variant="h4">Carbon Impact</Typography>
              <Typography variant="caption">{caption}</Typography>
              {carbonScore?.scoreType && (
                <Chip
                  size="small"
                  variant="soft"
                  color={QUALITY_CHIP_MAP[carbonScore.scoreType].color}
                  label={QUALITY_CHIP_MAP[carbonScore.scoreType].label}
                  sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                />
              )}
            </Stack>
          </Stack>
          {carbonScore && (
            <Typography variant="caption" color="text.secondary">
              {CARBON_DISCLAIMER}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={2}>
          <Divider/>
          <Typography>
            {carbonScore ? DESCRIPTION_MAP[carbonScore.scoreType] : 'No carbon data available.'}
          </Typography>
          <Stack gap={1}>
            {carbonScore && (
              <DetailRow
                label="Carbon Footprint for Product"
                value={
                  footprint !== null ? `${Number(footprint.toFixed(2))} kg CO₂e` : 'Uncomputable'
                }
              />
            )}
            {proxyData && (
              <>
                <DetailRow label="Category" value={proxyData.gpcClassification} />
                <DetailRow label="Source" value={proxyData.sourceDbName} />
                <DetailRow label="Database" value={proxyData.sourceDbId} />
              </>
            )}
          </Stack>

          <Link
            href={CARBON_HELP_URL}
            target="_blank"
            rel="noopener"
            variant="body2"
            color="text.secondary"
          >
            How is this calculated?
          </Link>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
